import * as ModalPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { FollowWindowBreakpoint } from '../ui/use-responsive'

const modalContentVariants = cva(
  'fixed left-[50%] top-[50%] z-50 grid w-auto h-auto max-h-[calc(100vh-2rem)] max-w-5xl overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-5 border p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
  {
    variants: {
      variant: {
        default: 'bg-background text-header border-border',
        contrast: 'bg-background-inverse text-header-inverse border-border-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const Modal = ModalPrimitive.Root

const ModalTrigger = forwardRef<
  ElementRef<typeof ModalPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof ModalPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Trigger ref={ref} className={cn('cursor-pointer', className)} {...props} />
))
ModalTrigger.displayName = ModalPrimitive.Trigger.displayName

const ModalPortal = ModalPrimitive.Portal

const ModalClose = ModalPrimitive.Close

const ModalOverlay = forwardRef<
  ElementRef<typeof ModalPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof ModalPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
ModalOverlay.displayName = ModalPrimitive.Overlay.displayName

export interface ModalContentProps
  extends ComponentPropsWithoutRef<typeof ModalPrimitive.Content>,
    VariantProps<typeof modalContentVariants> {
  submitFooter?: ReactNode // submitFooter is a sticky footer at the bottom of the page, not the modal
  wrapperClassName?: string // wrapperClassName is used to add a class to the wrapper div that contains the content
  closeButtonClassName?: string // closeButtonClassName is used to add a class to the X (close) button
}

const ModalContent = forwardRef<ElementRef<typeof ModalPrimitive.Content>, ModalContentProps>(
  ({ className, children, submitFooter, wrapperClassName, closeButtonClassName, variant, ...props }, ref) => (
    <ModalPortal>
      {/* Modal renders via portal to document.body, so we need to wrap it in a div with the select-app-mfe class since we are prefixing css */}
      <div className={wrapperClassName}>
        <ModalOverlay />
        <ModalPrimitive.Content ref={ref} className={cn(modalContentVariants({ variant }), className)} {...props}>
          <FollowWindowBreakpoint>{children}</FollowWindowBreakpoint>
          <ModalPrimitive.Close
            className={cn(
              'cursor-pointer absolute right-6 top-6 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-sub-surface-bg data-[state=open]:text-secondary',
              closeButtonClassName,
            )}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </ModalPrimitive.Close>
        </ModalPrimitive.Content>
        <FollowWindowBreakpoint>{submitFooter}</FollowWindowBreakpoint>
      </div>
    </ModalPortal>
  ),
)
ModalContent.displayName = ModalPrimitive.Content.displayName

const ModalHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
ModalHeader.displayName = 'ModalHeader'

const ModalFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:space-x-2', className)} {...props} />
)
ModalFooter.displayName = 'ModalFooter'

const ModalTitle = forwardRef<
  ElementRef<typeof ModalPrimitive.Title>,
  ComponentPropsWithoutRef<typeof ModalPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
ModalTitle.displayName = ModalPrimitive.Title.displayName

const ModalDescription = forwardRef<
  ElementRef<typeof ModalPrimitive.Description>,
  ComponentPropsWithoutRef<typeof ModalPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Description ref={ref} className={cn('text-sm text-secondary', className)} {...props} />
))
ModalDescription.displayName = ModalPrimitive.Description.displayName

export {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPortal,
  ModalTitle,
  ModalTrigger,
}
